/**
 * Le protocole de la barre, de « Plus », du chat, de l'aide et de la fin du live du masteur : pour
 * chaque geste, le service de `data/services` et le message de salle que l'ancien pied envoyait
 * (`common/header/LiveModeFooter.js`, `common/messenger/CommonChat.js`, `common/liveMode/LiveHelpModal.js`),
 * mêmes routes, mêmes champs, mêmes destinataires (décision 7 : protocole identique).
 *
 * Les gestes sont écrits ici une fois, sur des dépendances injectées (`call`, `send`, `navigate`…) :
 * les crochets de la salle les branchent sur `apiCall`, le moteur commun et la navigation, et chaque
 * ligne de `TUTOR_LIVE_CHROME_EQUIVALENCE` a son test qui rejoue le geste et lit l'appel et le message.
 */
import { liveService } from "../../../../../../data/services/liveService"
import { liveServiceV2 } from "../../../../../../data/services/liveServiceV2"
import { messengerService } from "../../../../../../data/services/messengerService"
import { type ApiCallParams, type ApiService } from "../../../../../../engine/shared/apiCall"
import { type RoomConnection } from "../../../../../../engine/live/room/roomMachine"
import { type SendPolicy, type SendResult } from "../../../../../../engine/live/room/roomSender"
import { DIALOG_CLOSE, dialogButtonEvent } from "../../../hooks/trackDialogButton"
import { type TrackedEvent } from "../../../hooks/trackEvent"
import { tutorChatRecipients } from "../../../../adapters/tutor/Live/tutorLiveChatAdapter"
import { TUTOR_END_DIALOG_WORDS } from "../../../../view-models/tutor/Live/tutorLiveChromeViewModel"

/** Une ligne de la table : le geste, d'où il vient, ce qu'il appelle, ce qu'il dit à la salle. */
export type TutorLiveChromeEquivalence = {
    gesture: string
    legacy: string
    /** Le service de `data/services`, et sa route. */
    service: string | null
    route: string | null
    /** Le message de salle (`useLiveKitEventSender`) : son nom, ses champs, ses destinataires. */
    message: { type: string; fields: string[]; to: "student" | "all" | "student_or_all" } | null
    /** Quand le message part, s'il ne part pas aussitôt à une salle connectée (sinon : abandonné hors connexion). */
    delivery?: string
    /** L'événement Amplitude, quand il y en a un. */
    event?: string
}

/**
 * Ce que la fin du live attend d'une salle en reprise avant de dire `finalizeLive` et de la quitter.
 *
 * L'ancien `useLiveKitEventSender` tentait l'envoi dans tout état sauf `disconnected`, une salle en reprise
 * comprise. Le moteur n'envoie qu'à une salle connectée (`roomSender`, `drop` hors connexion), et la sortie
 * (`leave`) qui suit aussitôt démonte la salle : sans attente, une reprise au moment de la clôture perdait le
 * message, et les élèves ne recevaient pas la fin du live. 4 s couvrent une reprise ordinaire du
 * SDK (le transport relancé, une à deux secondes) sans faire attendre le masteur devant le bouton qui tourne
 * plus que le temps d'un geste ; au-delà, la coupure est longue, et la clôture, déjà faite au serveur, ne doit
 * pas rester bloquée.
 */
export const FINALIZE_RESUME_WAIT_MS = 4000
/** Le pas auquel la fin relit l'état de la salle pendant cette attente. */
export const FINALIZE_RESUME_POLL_MS = 200

/** La salle se reprend (le SDK se reconnecte, ou le moteur la relance) : la fin peut l'attendre. */
function isResuming(connection: RoomConnection | null): boolean {
    return connection === "reconnecting" || connection === "connecting"
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Attend que la salle sorte de sa reprise, `timeoutMs` au plus ; vrai si elle est alors connectée. Une salle
 * déjà connectée, coupée ou fermée répond aussitôt.
 */
export async function awaitRoomResume(
    connection: () => RoomConnection | null,
    options: { timeoutMs?: number; pollMs?: number; sleep?: (ms: number) => Promise<void> } = {}
): Promise<boolean> {
    const { timeoutMs = FINALIZE_RESUME_WAIT_MS, pollMs = FINALIZE_RESUME_POLL_MS, sleep = defaultSleep } = options
    let waited = 0
    while (isResuming(connection()) && waited < timeoutMs) {
        await sleep(pollMs)
        waited += pollMs
    }
    return connection() === "connected"
}

export const TUTOR_LIVE_CHROME_EQUIVALENCE: TutorLiveChromeEquivalence[] = [
    {
        gesture: "Tous en Concentré",
        legacy: "LiveModeFooter.js:598-616",
        service: "liveServiceV2.setSoundModeV2",
        route: "POST api/v2/live-sessions/{id}/sound-mode {enabled: false, user_ids}",
        message: null,
    },
    {
        gesture: "Tous en Dialogue",
        legacy: "LiveModeFooter.js:617-633",
        service: "liveServiceV2.setSoundModeV2",
        route: "POST api/v2/live-sessions/{id}/sound-mode {enabled: true, user_ids}",
        message: null,
    },
    {
        gesture: "Baisser toutes les mains",
        legacy: "LiveModeFooter.js:636-645",
        service: "liveService.lowerAllHands",
        route: "POST api/v1/live-sessions/{id}/lower-all-hands",
        message: null,
    },
    {
        gesture: "Le bouton fin",
        legacy: "LiveModeFooter.js:710-721",
        service: null,
        route: null,
        message: null,
        event: "button.clicked.exit_live",
    },
    {
        gesture: "Clôturer et envoyer rapports",
        legacy: "LiveModeFooter.js:327-331, :367-393 ; useLiveKitEventSender.js (l'envoi tenté sauf `disconnected`)",
        service: "liveService.finalizeLiveSession, puis liveService.leaveLiveSession",
        route: "POST api/v1/live-sessions/{id}/finalize ; push study-mission-prescription ; POST …/leave",
        message: { type: "finalizeLive", fields: ["id"], to: "all" },
        delivery:
            "salle connectée : aussitôt ; en reprise : attend la reprise " +
            `${FINALIZE_RESUME_WAIT_MS / 1000} s au plus, puis part ; l'attente écoulée, le moteur l'abandonne ` +
            "(télémétrie) et la sortie se fait quand même ; " +
            "salle coupée : la sortie se fait sans attendre (l'ancien n'envoyait pas à une salle `disconnected`)",
        event: "dialog.button.clicked",
    },
    {
        gesture: "Quitter sans clôturer / Quitter le LIVE",
        legacy: "LiveModeFooter.js:293-297, :360-366",
        service: "liveService.leaveLiveSession",
        route: "POST api/v1/live-sessions/{id}/leave",
        message: null,
        event: "dialog.button.clicked",
    },
    {
        gesture: "Signaler un problème",
        legacy: "LiveHelpModal.js:66-110",
        service: "liveServiceV2.submitIssueReport",
        route: "POST api/v2/live-sessions/{id}/issue-reports {category, description, diagnostics.reconnections, platform, attachment_files}",
        message: null,
        event: "bug_report.submitted",
    },
    {
        gesture: "Le chat : ouvrir un fil",
        legacy: "CommonChat.js:226-272",
        service: "messengerService.getMessages, puis messengerService.getMarkRead",
        route: "GET api/messenger/threads/{threadId}/messages ; GET …/mark-read",
        message: null,
    },
    {
        gesture: "Le chat : messages plus anciens",
        legacy: "CommonChat.js:192-200",
        service: "messengerService.getPageMessages",
        route: "GET api/messenger/threads/{threadId}/messages/page/{pageId}",
        message: null,
    },
    {
        gesture: "Le chat : envoyer un texte",
        legacy: "CommonChat.js:202-224",
        service: "messengerService.postMessages",
        route: 'POST api/messenger/threads/{threadId}/messages {message, temporary_id: "message"}',
        message: {
            type: "newMessage",
            fields: ["_id", "text", "createdAt", "user._id", "user.name", "threadId"],
            to: "student_or_all",
        },
    },
    {
        gesture: "Le chat : écrire",
        legacy: "CommonChat.js:1590-1600",
        service: null,
        route: null,
        message: { type: "typing", fields: [], to: "student_or_all" },
    },
    {
        gesture: "Le chat : envoyer une photo ou un fichier",
        legacy: "CommonChat.js:819-866",
        service: "messengerService.postImage / messengerService.postDocument",
        route: "POST api/messenger/threads/{threadId}/images | /documents",
        message: { type: "newMessage", fields: ["(la ressource du serveur)"], to: "student_or_all" },
    },
    {
        gesture: "Le chat : envoyer un vocal",
        legacy: "CommonChat.js:906-956",
        service: "messengerService.postAudio",
        route: "POST api/messenger/threads/{threadId}/audio",
        message: { type: "newMessage", fields: ["(la ressource du serveur)"], to: "student_or_all" },
    },
    {
        gesture: "Le chat : toucher une pièce dans « Tous »",
        legacy: "CommonChat.js:624-631, :1334-1366",
        service: "messengerService.getActivityFromChat",
        route: "GET (getActivityFromChat) {id: le message}",
        message: null,
    },
    {
        gesture: "« Un problème ? » s'ouvre / un problème choisi",
        legacy: "TroubleshootingPanel.js:1090, :1202",
        service: null,
        route: null,
        message: null,
        event: "troubleshooting.panel.opened / troubleshooting.problem.selected",
    },
    {
        gesture: "L'aide : « J'ai autorisé le micro / la caméra »",
        legacy: "TroubleshootingPanel.js:199-256 (PermissionAction), PermissionBanner.js:104-127 (requestPermissions)",
        service: null,
        // Un écart voulu : l'ancien, l'appareil autorisé, relançait `requestPermissions` — un second
        // `getUserMedia({audio: true, video: true})` qui redemandait l'autre appareil au navigateur.
        route:
            "navigator.mediaDevices.getUserMedia({audio: true} | {video: true}), pistes coupées aussitôt ; " +
            "écart : l'ancien enchaînait getUserMedia({audio: true, video: true}) et redemandait l'autre appareil, " +
            "le nouveau ne redemande que l'appareil refusé",
        message: null,
    },
    {
        gesture: "L'aide : « Changer de micro / de caméra »",
        legacy: "TroubleshootingPanel.js:263-304 (DeviceChangeButton)",
        service: null,
        route: "la fenêtre des appareils (onglet micro ou caméra)",
        message: null,
    },
    {
        gesture: "Le chat : retour dans l'app",
        legacy: "CommonChat.js:173-190",
        service: "messengerService.getMessages",
        route: "GET api/messenger/threads/{threadId}/messages ; notifications « messages » effacées (natif)",
        message: null,
    },
    {
        gesture: "« Resynchroniser »",
        legacy: "NewStudentsListItem.js:480-484, :622-647",
        service:
            "liveServiceV2.getLiveSessionV2, liveServiceV2.getCallV2, schoolAssessmentsService.getSchoolAssessments (par le fil, `useTutorLiveFeed.refresh`)",
        route: "GET api/v2/live-sessions/{id} ; GET …/call ; GET school-assessments {student_id, upcoming: true}",
        message: null,
    },
    {
        gesture: "Prendre RDV avec un coordinateur",
        legacy: "LiveModeFooter.js:766-802 ; Utils.openCalendly",
        service: null,
        route: "Calendly trial-done, prérempli avec le premier élève",
        message: null,
    },
]

/** Ce que « J'ai autorisé… » lit du navigateur : `navigator.mediaDevices`, quand il existe. */
export type MediaDevicesLike = {
    getUserMedia?: (constraints: {
        audio?: boolean
        video?: boolean
    }) => Promise<{ getTracks: () => { stop: () => void }[] }>
} | null

/**
 * « J'ai autorisé le micro / la caméra » (`PermissionAction`, `TroubleshootingPanel.js:236-252`) : la demande
 * relancée au navigateur pour ce seul appareil, ses pistes coupées aussitôt. Vrai : autorisé ; faux : encore
 * refusé (ou pas de `mediaDevices`, que l'ancien lisait aussi comme un refus).
 */
export async function confirmMediaPermission(
    kind: "microphone" | "camera",
    mediaDevices: MediaDevicesLike | undefined
): Promise<boolean> {
    try {
        if (mediaDevices?.getUserMedia == null) return false
        const stream = await mediaDevices.getUserMedia(kind === "camera" ? { video: true } : { audio: true })
        for (const track of stream.getTracks()) track.stop()
        return true
    } catch {
        return false
    }
}

type Call = <T = unknown>(service: ApiService, params?: ApiCallParams) => Promise<T>
type Send = (
    type: string,
    data: Record<string, unknown>,
    to: number[] | null,
    policy: SendPolicy
) => Promise<SendResult>

export type TutorLiveChromeProtocolDeps = {
    call: Call
    send: Send
    track: (event: TrackedEvent) => void
    navigate: (route: string, params?: Record<string, unknown>) => void
    /** La fin de l'obstruction de la caméra, à écrire avant toute sortie (`closeOutCameraObstruction`). */
    closeOutObstruction: () => void
    /** La coupure de la salle en cours, refermée avant de sortir (`LiveRoom.closeOutage`). */
    closeOutage: () => void
    /** Le mode live éteint (`useDisconnectFromLive`) et les tranches de la satisfaction remises au repos. */
    disconnect: () => void
    /** L'état de la salle, lu au geste (`LiveRoom.state.connection`) ; `null` sans salle. */
    connection: () => RoomConnection | null
    /** Les tests passent la leur ; sinon `setTimeout`. */
    sleep?: (ms: number) => Promise<void>
}

/** Le service comme `apiCall` le prend : les fonctions JS de `data/services` sont typées `any`. */
const service = (fn: unknown) => fn as ApiService

export const TUTOR_LIVE_EVENTS = {
    exitLive: { name: "button.clicked.exit_live" },
    finalize: dialogButtonEvent(TUTOR_END_DIALOG_WORDS.openTitle, TUTOR_END_DIALOG_WORDS.finalize),
    leaveOpen: dialogButtonEvent(TUTOR_END_DIALOG_WORDS.openTitle, TUTOR_END_DIALOG_WORDS.leaveOpen),
    openClosed: dialogButtonEvent(TUTOR_END_DIALOG_WORDS.openTitle, DIALOG_CLOSE),
    leaveFinalized: dialogButtonEvent(TUTOR_END_DIALOG_WORDS.finalizedTitle, TUTOR_END_DIALOG_WORDS.leave),
    finalizedClosed: dialogButtonEvent(TUTOR_END_DIALOG_WORDS.finalizedTitle, DIALOG_CLOSE),
    panelOpened: { name: "troubleshooting.panel.opened" },
    problemSelected: { name: "troubleshooting.problem.selected" },
    reportOpened: { name: "bug_report.modal.opened" },
    reportSubmitted: { name: "bug_report.submitted" },
} satisfies Record<string, TrackedEvent>

export type TutorChatUser = { id: number | string | null | undefined; name: string | null | undefined }

/** Les gestes, sur leurs dépendances. Chacun rend la promesse de son appel. */
export function tutorLiveChromeProtocol(deps: TutorLiveChromeProtocolDeps) {
    const { call, send, track, navigate } = deps

    const leaveCall = (liveSessionId: number) =>
        call(service(liveService.leaveLiveSession), { routeParams: { id: liveSessionId } }).then(() => {
            // `useApiStatus(leaveLiveSession)` du pied : l'obstruction refermée avant que le journal soit détruit.
            deps.closeOutObstruction()
            deps.closeOutage()
            deps.disconnect()
        })

    return {
        /** « Tous en Concentré » / « Tous en Dialogue » : tous les élèves inscrits, présents ou non. */
        setAllSoundMode: (liveSessionId: number, enabled: boolean, studentIds: (number | string)[]) =>
            call(service(liveServiceV2.setSoundModeV2), {
                routeParams: { id: liveSessionId },
                data: { enabled, user_ids: studentIds },
            }),

        lowerAllHands: (liveSessionId: number) =>
            call(service(liveService.lowerAllHands), { routeParams: { id: liveSessionId } }),

        /** Le bouton fin : la boîte s'ouvre, la mesure part. */
        exitPressed: () => track(TUTOR_LIVE_EVENTS.exitLive),

        /**
         * « Clôturer et envoyer rapports », dans l'ordre de l'ancien pied (`:367-393`) : la clôture,
         * puis la fin de l'obstruction, `finalizeLive {id: tuteur}` à toute la salle, la prescription
         * (`study-mission-prescription`, qui mène au bilan), et la sortie de l'appel (`leave`) — on quitte
         * avant les rapports : sur iOS, l'enregistreur démarré pendant l'appel n'entendait rien.
         *
         * Une salle en reprise est attendue `FINALIZE_RESUME_WAIT_MS` au plus avant `finalizeLive` : la sortie
         * qui suit la démonte, le message ne partirait jamais. Les autres messages gardent leur politique.
         */
        finalize: async (
            liveSessionId: number,
            tutorId: number | string | null | undefined,
            /**
             * La clôture est acquise et la prescription va s'ouvrir : la boîte de fin se ferme là. Fermée après la
             * sortie de l'appel, qui suit, elle restait par-dessus la prescription (« déjà clôturé »).
             */
            onFinalized?: () => void
        ) => {
            const occurrence = await call<{ id?: number | string } | null>(service(liveService.finalizeLiveSession), {
                routeParams: { id: liveSessionId },
            })
            deps.closeOutObstruction()
            await awaitRoomResume(deps.connection, { sleep: deps.sleep })
            void send("finalizeLive", { id: tutorId }, null, "drop")
            onFinalized?.()
            navigate("study-mission-prescription", { occurrence_id: occurrence?.id, liveSessionId })
            await leaveCall(liveSessionId)
        },

        /** « Quitter sans clôturer », « Quitter le LIVE ». */
        leave: (liveSessionId: number) => leaveCall(liveSessionId),

        submitIssueReport: (liveSessionId: number, data: Record<string, unknown>) =>
            call(service(liveServiceV2.submitIssueReport), { routeParams: { id: liveSessionId }, data }).then(
                (result) => {
                    track(TUTOR_LIVE_EVENTS.reportSubmitted)
                    return result
                }
            ),

        /** Le fil relu (la page la plus récente, enveloppe comprise : sa `meta` dit la page suivante). */
        readThread: <T = unknown>(threadId: string) =>
            call<T>(service(messengerService.getMessages), { routeParams: { threadId }, whole: true }),

        markRead: (threadId: string) => call(service(messengerService.getMarkRead), { routeParams: { threadId } }),

        readEarlier: <T = unknown>(threadId: string, pageId: string | number) =>
            call<T>(service(messengerService.getPageMessages), { routeParams: { threadId, pageId }, whole: true }),

        /** Un texte : la messagerie le range, puis la salle l'annonce à l'élève du fil, ou à tous. */
        sendText: async (args: {
            channel: string
            threadId: string
            temporaryId: string
            text: string
            createdAt: string
            user: TutorChatUser
        }) => {
            const resource = await call<Record<string, unknown> | null>(service(messengerService.postMessages), {
                routeParams: { threadId: args.threadId },
                data: { message: args.text, temporary_id: "message" },
                whole: true,
            })
            void send(
                "newMessage",
                {
                    _id: args.temporaryId,
                    text: args.text,
                    createdAt: args.createdAt,
                    user: { _id: args.user.id, name: args.user.name },
                    threadId: args.threadId,
                },
                tutorChatRecipients(args.channel),
                "drop"
            )
            return resource
        },

        typing: (channel: string) => send("typing", {}, tutorChatRecipients(channel), "drop"),

        /** Une photo ou un document : la ressource du serveur, annoncée telle quelle. */
        sendAttachment: async (args: {
            channel: string
            threadId: string
            kind: "image" | "document"
            body: unknown
        }) => {
            const resource = await call<Record<string, unknown> | null>(
                service(args.kind === "image" ? messengerService.postImage : messengerService.postDocument),
                { routeParams: { threadId: args.threadId }, data: args.body, whole: true }
            )
            if (resource != null) void send("newMessage", resource, tutorChatRecipients(args.channel), "drop")
            return resource
        },

        /** Un vocal parti par la file des vocaux : sa ressource s'annonce comme une pièce. */
        announceVoice: (channel: string, resource: Record<string, unknown>) =>
            send("newMessage", resource, tutorChatRecipients(channel), "drop"),

        activityFromChat: <T = unknown>(messageId: string) =>
            call<T>(service(messengerService.getActivityFromChat), { routeParams: { id: messageId } }),
    }
}

export type TutorLiveChromeProtocol = ReturnType<typeof tutorLiveChromeProtocol>
